export default {
    page: {
        title: 'Feedback Cycles',
        subtitle: "Manage your organization's reference periods and objective cycles.",
    },
    header: {
        heading: 'Getting Started',
        description:
            'Create feedback cycles for your organization to ensure that {{managerPluralLabel}} and employees automatically receive their assigned activities.',
        secondaryDescription:
            '360 feedback will first be shared with {{managerPluralLabel}}, allowing them to review participants and customize questions as needed.',
    },
    tabsAriaLabel: 'Feedback cycles dashboard sections',
    tabs: {
        evaluationPeriods: 'Reference periods',
        progressTracking: 'Progress tracking',
        objectivePeriods: 'Objective periods',
    },
    evaluationCycles: {
        title: 'Feedback cycles',
        description: 'Define the time periods covered by each review cycle.',
        searchPlaceholder: 'Search a cycle',
        createTemplate: 'Create reference period',
    },
    filters: {
        status: 'Status',
        evaluationType: 'Type',
    },
    status: {
        draft: 'Draft',
        inactive: 'Inactive',
        active: 'Active',
        deactivated: 'Deactivated',
        completed: 'Completed',
    },
    type: {
        evaluation360: '360 Feedback',
        selfEvaluation: 'Self-Assessment',
        managerEvaluation: '{{managerLabelCapitalized}} Assessment',
    },
    typeBadge: {
        managerAssessment: '{{managerLabelCapitalized}} assessment',
        feedback360: '360 feedback',
        selfAssessment: 'Self-assessment',
    },
    evaluationPeriod: {
        range: '{{start}} - {{end}}',
        notSet: 'To be defined',
    },
    comingSoonTooltip: 'This feature will be available soon.',
    progressTracking: {
        title: 'Progress tracking',
        description: "Follow how your organization's evaluation is progressing.",
        filters: {
            cycle: 'Cycle',
            evaluationType: 'Evaluation type',
        },
        metric: 'Progress',
        submittedOfTotal: '{{submitted}} of {{total}} evaluation submissions',
        breakdown: {
            completed: 'Completed',
            inProgress: 'In progress',
            notStarted: 'Not started',
        },
        breakdownLabel: '{{label}}:',
        emptyState: 'No evaluation has been generated yet for this selection.',
        team: {
            title: 'Your team',
            comingSoon: 'Dynamic data coming soon.',
        },
    },
    message: {
        deleteSuccess: 'Feedback cycle deleted successfully.',
        activateSuccess: 'Feedback cycle activated successfully.',
        deactivateSuccess: 'Feedback cycle deactivated successfully.',
        renameSuccess: 'Feedback cycle renamed successfully.',
        reactivateSkippedMilestones:
            'Feedback cycle activated successfully. Overdue milestones skipped: {{count}}.',
        reactivateSkippedReminders:
            'Feedback cycle activated successfully. Overdue reminders skipped: {{count}}.',
        reactivateSkippedReminders_one:
            'Feedback cycle activated successfully. Overdue reminder skipped: {{count}}.',
        reactivateSkippedReminders_other:
            'Feedback cycle activated successfully. Overdue reminders skipped: {{count}}.',
        deleteGroupSuccessWithEvaluations:
            'Feedback cycle deleted successfully, along with {{generatedCount}} generated evaluation item(s).',
    },
    emptyList: 'No feedback cycle yet. Create one to start a performance review round.',
    noResultsFound: 'No results found',
    menuItem: {
        seeSentCycles: 'See Sent Cycles',
        reactivate: 'Reactivate',
        sendReminders: 'Send Reminders',
    },
    rename: {
        title: 'Rename cycle',
    },
    sendReminders: {
        confirmMessage: 'Send a reminder to the {{count}} employees who have not answered yet?',
        confirmMessage_one: 'Send a reminder to the {{count}} employee who has not answered yet?',
        confirmMessage_other:
            'Send a reminder to the {{count}} employees who have not answered yet?',
        successMessage: 'Reminder sent to {{count}} employees.',
        successMessage_one: 'Reminder sent to {{count}} employee.',
        successMessage_other: 'Reminder sent to {{count}} employees.',
        emptyMessage: 'Everyone has answered. There is nobody to remind.',
        nothingSentMessage: 'Nothing has been sent yet. There is nobody to remind.',
    },
    deactivateGroup: {
        confirmTitle: 'Deactivate this cycle?',
        confirmMessage:
            'Scheduled emails stop going out until you reactivate it. Evaluations already generated are untouched.',
    },
    reactivateGroup: {
        confirmTitle: 'Reactivate this cycle?',
        confirmMessage:
            '{{milestones}} and {{reminders}} will be skipped. Only future dates resume.',
        skippedMilestones_one: '{{count}} overdue milestone',
        skippedMilestones_other: '{{count}} overdue milestones',
        skippedReminders_one: '{{count}} overdue reminder',
        skippedReminders_other: '{{count}} overdue reminders',
        nothingSkippedMessage: 'No overdue milestone or reminder will be skipped.',
        skippedRemindersTitle: 'Feedback cycle activated',
    },
    deleteGroup: {
        confirmTitle: 'Delete this cycle?',
        confirmMessage:
            'This cycle and any evaluation it has already generated will be deleted, and any of its scheduled emails will be cancelled. This cannot be undone.',
        confirmMessageWithCounts:
            'This cycle and its {{generatedCount}} generated evaluation item(s) will be deleted, and its {{scheduledEmailsCount}} scheduled email(s) will be cancelled. This cannot be undone.',
        blockedTitle: 'This cycle cannot be deleted',
        blockedMessage:
            '{{submittedCount}} of its {{generatedCount}} generated evaluation items already carry a submission, so deleting it would destroy real results. Deactivate it instead to stop it without losing anything.',
        blockedMessageCompleted:
            '{{submittedCount}} of its {{generatedCount}} generated evaluation items already carry a submission, so deleting it would destroy real results.',
        blockedMessageDeactivated: 'The cycle is already deactivated, and data is not lost.',
    },
    evaluationTemplateLabel: 'Evaluation template',
    sentCycles: {
        title: 'Sent cycles: {{cycleName}}',
        subtitle360: 'Overview of launched 360 cycles and their progress.',
        subtitleSelf: 'Overview of launched self-assessment cycles and their progress.',
        subtitleManager:
            'Overview of launched {{managerLabel}} assessment cycles and their progress.',
        columns: {
            launchDate: 'Launch date',
            peopleEvaluated: 'Employees evaluated',
            managerLaunchRate: '{{managerLabelCapitalized}} launch rate',
            completionRate: 'Completion rate',
            actions: 'Actions',
        },
        seeLaunchedSurveys: 'See launched surveys',
        footerNote:
            'Percentages may change. They are updated when {{managerPluralLabel}} prepare the 360 feedback. The number of employees and respondents can be edited by {{managerPluralLabel}}. {{managerPluralLabel}} can also remove evaluations if an employee is not concerned.',
    },
    launchedSurveys: {
        title: 'Launched surveys',
        subtitle: 'Completion rate for each 360 feedback for {{cycleName}}.',
        columns: {
            evaluatedEmployee: 'Employee evaluated',
            role: 'Role',
            numberOfReviewers: 'Number of reviewers',
            completionRate: 'Completion rate',
            status: 'Status',
        },
        status: {
            completed: 'Completed',
            inProgress: 'In progress',
            notStarted: 'Not started',
        },
        downloadExcel: 'Download in Excel',
        footerNote:
            'Percentages may change. They are updated when {{managerPluralLabel}} prepare the 360 feedback. The number of evaluated people and reviewers can be edited by {{managerPluralLabel}}.',
    },
    managerLaunchStatus: {
        title: '{{managerLabelCapitalized}} launch status',
        subtitle:
            '{{launched}} of {{total}} {{managerPluralLabel}} have launched their 360 surveys.',
        notLaunchedYet: 'Not launched yet ({{count}})',
        launched: 'Launched ({{count}})',
        columns: {
            manager: '{{managerLabel}}',
            launchedRequired: 'Launched / Required reviews',
            launchedOn: 'Launched on',
        },
        downloadExcel: 'Download in Excel',
        footerNote:
            'Percentages may change. They are updated when {{managerPluralLabel}} prepare the 360 feedback. The number of evaluated people and reviewers can be edited by {{managerPluralLabel}}.',
    },
};
